import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'step',
    'nextButton',
    'prevLink',
    'otpForm',
    'sandboxHint',
    'stepIndicator',
    'stepTitle',
    'stepperSegment',
    'documentBar',
    'pageCount',
  ]

  static values = {
    currentStep: { type: Number, default: 0 },
    totalSteps: Number,
    otpUrl: String,
    docTitles: Array,
    docIds: Array,
  }

  connect() {
    this.scrolledSteps = new Set()
    // Intro step has nothing to read — auto-eligible.
    this.scrolledSteps.add(0)
    this.showStep(0)
  }

  docScrolled() {
    this.scrolledSteps.add(this.currentStepValue)
    this.enableNextButton()
  }

  // The page count only exists once pdf.js has parsed the file, and the bar
  // that shows it sits in the header — outside the viewer's scope. Matching on
  // the document id rather than on the current step keeps the label on the
  // right bar even if the reader moved on while the file was still rendering.
  updatePageCount({ detail }) {
    const index = this.docIdsValue.indexOf(detail.docId)
    const label = this.pageCountTargets[index]
    if (!label) return

    label.textContent = detail.label
  }

  nextStep() {
    if (this.currentStepValue < this.totalStepsValue - 1) {
      this.showStep(this.currentStepValue + 1)
    }
  }

  prevStep() {
    if (this.currentStepValue > 0) {
      this.showStep(this.currentStepValue - 1)
    }
  }

  async sendOtp() {
    const response = await fetch(this.otpUrlValue, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
    })

    if (response.ok) {
      const data = await response.json()
      if (data.sandbox_code && this.hasSandboxHintTarget) {
        this.sandboxHintTarget.textContent = data.sandbox_code
        this.sandboxHintTarget
          .closest('[data-sandbox-wrapper]')
          .classList.remove('hidden')
      }
    }

    this.showStep(this.totalStepsValue - 1)

    if (this.hasOtpFormTarget) {
      this.otpFormTarget.classList.remove('hidden')
      this.otpFormTarget.classList.add('flex')
    }
  }

  showStep(index) {
    this.currentStepValue = index

    this.stepTargets.forEach((step, i) => {
      step.classList.toggle('hidden', i !== index)
    })

    if (this.scrolledSteps.has(index)) {
      this.enableNextButton()
    } else {
      this.disableNextButton()
    }

    this.updatePrevLink()
    this.updateDocumentBar()
    this.updateStepIndicator()
    this.updateStepperSegments()
  }

  enableNextButton() {
    const btn = this.currentNextButton()
    if (!btn) return
    btn.disabled = false
  }

  disableNextButton() {
    const btn = this.currentNextButton()
    if (!btn) return
    btn.disabled = true
  }

  currentNextButton() {
    return this.nextButtonTargets[this.currentStepValue]
  }

  updatePrevLink() {
    // prevLink targets start at step 1 — intro has none.
    const prevLinkIndex = this.currentStepValue - 1
    this.prevLinkTargets.forEach((link, i) => {
      const visible = i === prevLinkIndex && this.currentStepValue > 0
      link.classList.toggle('hidden', !visible)
    })
  }

  // Document bars start at step 1 — the intro and the code steps have none, and
  // the header falls back to two rows there.
  updateDocumentBar() {
    const barIndex = this.currentStepValue - 1
    this.documentBarTargets.forEach((bar, i) => {
      bar.classList.toggle('hidden', i !== barIndex)
    })
  }

  updateStepIndicator() {
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent =
        `${this.currentStepValue + 1} / ${this.totalStepsValue}`
    }
    if (this.hasStepTitleTarget) {
      this.stepTitleTarget.textContent =
        this.docTitlesValue[this.currentStepValue] || ''
    }
  }

  updateStepperSegments() {
    this.stepperSegmentTargets.forEach((segment, i) => {
      const active = i <= this.currentStepValue
      segment.classList.toggle('bg-primary', active)
      segment.classList.toggle('bg-base-300', !active)
    })
  }
}
